/**
 * useBackNavigation
 *
 * Intercepts the Android hardware back button (and browser back) so the app
 * navigates within its own route stack rather than exiting or going to a blank
 * page.
 *
 * Strategy:
 *   - On mount, push a sentinel `history` state so the browser has something
 *     to "pop back to" before leaving the app.
 *   - On every route change, push another state so each screen is addressable.
 *   - Listen to the `popstate` event. When it fires (hardware back pressed or
 *     browser back), call `onBack()` instead of letting the browser navigate.
 *   - Immediately re-push the sentinel so the back button is always armed.
 *
 * On desktop browsers the back button does nothing observable (the history
 * stack is managed entirely by this hook and never lets the URL actually change).
 *
 * Usage:
 *   Call once at the top level of App with the current route and a callback
 *   that handles "go back from this route".
 */

import { createEffect, onCleanup, onMount } from 'solid-js';

type BackHandler = () => void;

/**
 * @param onBack  Called when the hardware/browser back button is pressed.
 *                Should navigate to the previous screen or show the exit dialog.
 * @param routeKey  A string that changes whenever the active route changes.
 *                  Used to keep the history stack in sync.
 */
export function useBackNavigation(onBack: BackHandler, routeKey: string): void {
  const SENTINEL = { chorepop: true };

  function arm() {
    // Push so there's always a state to pop back to.
    history.pushState(SENTINEL, '');
  }

  onMount(() => {
    arm();

    const handlePopState = (e: PopStateEvent) => {
      // The browser popped back -- intercept it.
      e.preventDefault?.();
      // Re-arm immediately so the next back press also fires popstate.
      arm();
      // Delegate to the app's back handler.
      onBack();
    };

    window.addEventListener('popstate', handlePopState);
    onCleanup(() => window.removeEventListener('popstate', handlePopState));
  });

  // Re-arm whenever the route changes so the stack stays correct.
  createEffect(() => {
    // Depend on routeKey so this runs on every route transition.
    void routeKey;
    arm();
  });
}
